/**
 * District Detail Page — Route Module
 *
 * The district hub: the localized district name as H1, its inventory counts,
 * and the district→estate internal-linking mesh (the estate list, linked
 * through the shared `getEstateHref` helper so district hubs and the estates
 * index can never emit different URLs for the same estate).
 */
import { getEstateHref } from '@/shared/lib/estates/url';
import { routes } from '@letsgethome/routes';
import { plaintextString } from '@letsgethome/types/branded';
import { PageContainer } from '@letsgethome/ui/page-container';
import { useLoaderData } from 'react-router';

import { Breadcrumbs } from '../../../../shared/components/Breadcrumbs';
import { SeoHead } from '../../../../shared/components/SeoHead';
import { Link } from '../../../../shared/i18n/routing';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import type { loader } from './detail.loader';

export function Component() {
  const { district, seo } = useLoaderData<typeof loader>();
  const t = useTranslations('estates');

  return (
    <>
      <SeoHead {...seo} />
      <div className="min-h-screen bg-background">
        <PageContainer size="7xl" className="pt-6 sm:px-6 lg:px-8">
          <Breadcrumbs items={seo.breadcrumbs} />
          {/* Region name links UP to the index — no region page exists, so the
              hub's parent in the mesh is `/districts`. */}
          <Link
            href={routes.districts.index()}
            prefetch="intent"
            className="text-sm font-medium text-success-text hover:underline"
          >
            {plaintextString(district.regionName)}
          </Link>
          <h1 className="mt-1 text-3xl font-bold text-foreground sm:text-4xl">
            {plaintextString(district.name)}
          </h1>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>{t('showing', { count: district.estateCount })}</span>
            <span className="text-success-text">
              {t('detail.listingCount', { count: district.activeListingCount })}
            </span>
          </div>
          {/* The hub's links DOWN to its own two facets (SEO F9). The facets
              are sitemap-enumerated and were reachable from nowhere else: an
              inbound link is what earns them crawl priority and lets the hub's
              authority flow into them. The district hub is the one page a
              visitor and a crawler both expect to find them on. */}
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              href={routes.districts.rent(district.slug)}
              prefetch="intent"
              className="rounded-lg bg-success/10 px-4 py-2 text-sm font-medium text-success-text transition-colors hover:bg-success/20"
            >
              {t('districtFacet.rentLink', { district: plaintextString(district.name) })}
            </Link>
            <Link
              href={routes.districts.buy(district.slug)}
              prefetch="intent"
              className="rounded-lg bg-info/10 px-4 py-2 text-sm font-medium text-info-text transition-colors hover:bg-info/20"
            >
              {t('districtFacet.buyLink', { district: plaintextString(district.name) })}
            </Link>
          </div>
        </PageContainer>

        <PageContainer as="section" size="7xl" className="py-10 sm:px-6 lg:px-8">
          <h2 className="mb-6 text-2xl font-bold text-foreground">{t('title')}</h2>
          {district.estates.length > 0 ? (
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {district.estates.map((estate) => (
                <li key={estate.id}>
                  <Link
                    href={getEstateHref(estate)}
                    prefetch="intent"
                    className="group block rounded-xl bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <h3 className="font-semibold text-foreground transition-colors group-hover:text-success-text">
                      {plaintextString(estate.name)}
                    </h3>
                    {estate.streetAddress && (
                      <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                        {plaintextString(estate.streetAddress)}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      {estate.buildingsCount > 0 && (
                        <span>{t('card.blocks', { count: estate.buildingsCount })}</span>
                      )}
                      {estate.activeSaleCount > 0 && (
                        <span className="text-info-text">
                          {t('card.forSale', { count: estate.activeSaleCount })}
                        </span>
                      )}
                      {estate.activeRentCount > 0 && (
                        <span className="text-success-text">
                          {t('card.forRent', { count: estate.activeRentCount })}
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-muted-foreground">{t('noEstates')}</p>
          )}
        </PageContainer>
      </div>
    </>
  );
}
